/**
 * PostHog Provider Component
 *
 * Initializes PostHog on mount and tracks page views on route changes.
 * Place inside AuthProvider in the component tree.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import {
  init_posthog,
  get_posthog,
  is_turbo_stream_decode_error,
} from "~/lib/posthog.client";
import type { ReactNode } from "react";

interface PostHogProviderProps {
  children: ReactNode;
}

/**
 * Cooldown (ms) between hard-navigation recoveries for turbo-stream decode
 * failures (ORC-388). Prevents reload loops if the underlying response is
 * persistently malformed for the current URL.
 */
const TURBO_STREAM_RECOVERY_COOLDOWN_MS = 30_000;
const TURBO_STREAM_RECOVERY_KEY = "rr_turbo_stream_recovery_at";

/**
 * PostHog provider that handles initialization and page view tracking.
 *
 * Features:
 * - Initializes PostHog on client-side mount
 * - Tracks $pageview events on route changes
 * - Recovers from React Router turbo-stream decode failures via hard reload
 * - Does not add any UI elements
 */
export function PostHogProvider({ children }: PostHogProviderProps) {
  const location = useLocation();
  const prev_pathname = useRef<string | null>(null);

  // Initialize PostHog on mount
  useEffect(() => {
    init_posthog();
  }, []);

  // Recovery listener for React Router single-fetch turbo-stream decode
  // failures (ORC-388). When a `.data` request returns a non-stream payload
  // mid-navigation, React Router catches the error internally but the user
  // can be left on the previous page with a stale view. We listen for the
  // surfaced exception and trigger a hard navigation to the current URL,
  // which always returns the full HTML document and recovers cleanly.
  useEffect(() => {
    if (typeof window === "undefined") return;

    function should_recover(message: string | undefined): boolean {
      if (!is_turbo_stream_decode_error(undefined, message)) return false;
      // Cooldown — avoid reload loops if the underlying response is
      // persistently broken for the current URL.
      try {
        const last = sessionStorage.getItem(TURBO_STREAM_RECOVERY_KEY);
        if (last) {
          const elapsed = Date.now() - Number(last);
          if (elapsed >= 0 && elapsed < TURBO_STREAM_RECOVERY_COOLDOWN_MS) {
            return false;
          }
        }
      } catch {
        // sessionStorage may be unavailable (private mode, SSR shim);
        // fall through and attempt recovery anyway.
      }
      return true;
    }

    function trigger_recovery(): void {
      try {
        sessionStorage.setItem(
          TURBO_STREAM_RECOVERY_KEY,
          String(Date.now()),
        );
      } catch {
        // ignore — recovery still proceeds
      }
      // Hard navigation to the URL the user was attempting to reach. If the
      // location hasn't changed yet (RR aborted before commit), this is
      // equivalent to a reload.
      window.location.assign(window.location.href);
    }

    function handle_error(event: ErrorEvent): void {
      const message =
        event.error instanceof Error
          ? event.error.message
          : typeof event.message === "string"
            ? event.message
            : undefined;
      if (should_recover(message)) {
        trigger_recovery();
      }
    }

    function handle_rejection(event: PromiseRejectionEvent): void {
      const reason = event.reason as unknown;
      let message: string | undefined;
      if (reason instanceof Error) {
        message = reason.message;
      } else if (typeof reason === "string") {
        message = reason;
      } else if (
        reason &&
        typeof reason === "object" &&
        "message" in reason &&
        typeof (reason as { message?: unknown }).message === "string"
      ) {
        message = (reason as { message: string }).message;
      }
      if (should_recover(message)) {
        trigger_recovery();
      }
    }

    window.addEventListener("error", handle_error);
    window.addEventListener("unhandledrejection", handle_rejection);
    return () => {
      window.removeEventListener("error", handle_error);
      window.removeEventListener("unhandledrejection", handle_rejection);
    };
  }, []);

  // Track page views on route changes
  useEffect(() => {
    const posthog = get_posthog();

    // Skip if not initialized or if pathname hasn't changed
    if (!posthog || prev_pathname.current === location.pathname) {
      prev_pathname.current = location.pathname;
      return;
    }

    prev_pathname.current = location.pathname;

    // Capture page view event
    posthog.capture("$pageview", {
      $current_url: window.location.href,
      path: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [location.pathname, location.search, location.hash]);

  return <>{children}</>;
}
